import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { convertRowDateTime, msToShortDuration } from '@/lib/dateTime';
import { TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2Icon } from 'lucide-react';
import { useOpenPlayerModal } from "@/hooks/playerModal";
import { PlayersTableSearchResp, PlayersTableFiltersType, PlayersTableSearchType, PlayersTableSortingType, PlayersTablePlayerType } from '@shared/playerApiTypes';
import { useBackendApi } from '@/hooks/fetch';
import Avatar from '@/components/Avatar';
import { Badge } from '@/components/ui/badge';

/**
 * Player row
 */
type PlayerRowProps = {
    rowData: PlayersTablePlayerType;
    modalOpener: ReturnType<typeof useOpenPlayerModal>;
}

function PlayerRow({ rowData, modalOpener }: PlayerRowProps) {
    const openModal = () => {
        modalOpener({ license: rowData.license });
    }

    return (
        <TableRow 
            onClick={openModal} 
            className='group cursor-pointer border-none hover:bg-transparent transition-all duration-200'
        >
            {/* Column 1: Avatar + Name + Badges */}
            <TableCell 
                className={cn(
                    'px-4 py-3 bg-[#0c0e16]/60 border-y border-l border-white/[0.04] rounded-l-xl transition-all duration-200',
                    'group-hover:bg-[#121622]/80 group-hover:border-y-[#00ffd1]/20 group-hover:border-l-[#00ffd1]/20',
                )}
            >
                <div className='flex items-center gap-3'>
                    {/* Avatar with Status Ring */}
                    <div className="relative shrink-0">
                        <Avatar 
                            username={rowData.displayName} 
                            profilePicture={rowData.profilePicture} 
                            className="w-10 h-10 rounded-full border border-white/[0.08] shadow-sm"
                        />
                        {/* Status badge pip */}
                        <span 
                            className={cn(
                                "absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-[3px] border-[#090c14] ring-1",
                                rowData.isOnline 
                                    ? "bg-[#00ffd1] ring-[#00ffd1]/45 animate-pulse" 
                                    : "bg-slate-500 ring-slate-500/45"
                            )}
                        />
                    </div>

                    <div className="flex flex-col min-w-0 text-left">
                        <span className='font-bold text-[13.5px] text-white tracking-tight group-hover:text-[#00ffd1] transition-colors duration-150 truncate max-w-[180px]'>
                            {rowData.displayName}
                        </span>
                        
                        {/* Badges / License */}
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-[10px] font-mono text-slate-500 tracking-wider uppercase select-none shrink-0">
                                {rowData.license.substring(0, 8)}...
                            </span>
                            {rowData.isAdmin && (
                                <Badge 
                                    variant="outline" 
                                    className="bg-amber-500/10 border-amber-500/20 text-amber-400 font-mono text-[9px] px-1 py-0 select-none uppercase tracking-wider"
                                >
                                    Admin
                                </Badge>
                            )}
                            {rowData.isWhitelisted && (
                                <Badge 
                                    variant="outline" 
                                    className="bg-[#00ffd1]/10 border-[#00ffd1]/20 text-[#00ffd1] font-mono text-[9px] px-1 py-0 select-none uppercase tracking-wider"
                                >
                                    WL
                                </Badge>
                            )}
                            {rowData.notes && (
                                <Badge 
                                    variant="outline" 
                                    className="bg-purple-500/10 border-purple-500/20 text-purple-400 font-mono text-[9px] px-1 py-0 select-none uppercase tracking-wider"
                                >
                                    Note
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
            </TableCell>

            {/* Column 2: Play Time */}
            <TableCell 
                className={cn(
                    'px-4 py-3 bg-[#0c0e16]/60 border-y border-white/[0.04] font-mono text-xs font-semibold text-slate-300 transition-all duration-200 text-left',
                    'group-hover:bg-[#121622]/80 group-hover:border-y-[#00ffd1]/20 group-hover:text-white',
                )}
            >
                {msToShortDuration(rowData.playTime * 60_000)}
            </TableCell>

            {/* Column 3: First Joined */}
            <TableCell 
                className={cn(
                    'px-4 py-3 bg-[#0c0e16]/60 border-y border-white/[0.04] text-xs text-slate-400 font-mono transition-all duration-200 text-left',
                    'group-hover:bg-[#121622]/80 group-hover:border-y-[#00ffd1]/20 group-hover:text-slate-200',
                )}
            >
                {convertRowDateTime(rowData.tsJoined)}
            </TableCell>

            {/* Column 4: Last Connection */}
            <TableCell 
                className={cn(
                    'px-4 py-3 bg-[#0c0e16]/60 border-y border-r border-white/[0.04] rounded-r-xl text-xs text-slate-400 font-mono transition-all duration-200 text-left',
                    'group-hover:bg-[#121622]/80 group-hover:border-y-[#00ffd1]/20 group-hover:border-r-[#00ffd1]/20 group-hover:text-slate-200',
                )}
            >
                {convertRowDateTime(rowData.tsLastConnection)}
            </TableCell>
        </TableRow>
    )
}

/**
 * Last row
 */
type LastRowProps = {
    playersCount: number;
    hasReachedEnd: boolean;
    loadError: string | null;
    isFetching: boolean;
    retryFetch: (_reset?: boolean) => Promise<void>;
}

function LastRow({ playersCount, hasReachedEnd, isFetching, loadError, retryFetch }: LastRowProps) {
    let content: React.ReactNode;
    if (isFetching) {
        content = <Loader2Icon className="mx-auto animate-spin text-[#00ffd1]" />
    } else if (loadError) {
        content = <>
            <span className='text-destructive-inline'>Error: {loadError}</span><br />
            <button className='underline text-slate-400 hover:text-white transition-colors mt-1' onClick={() => retryFetch()}>Try again?</button>
        </>
    } else if (hasReachedEnd) {
        content = <span className='font-bold text-slate-500 tracking-wider text-xs uppercase'>
            {playersCount ? 'You have reached the end of the list.' : 'No players found.'}
        </span>
    } else {
        content = <span>
            You've found the end of the rainbow, but there's no pot of gold here. <br />
            <i>(this is an unexpected state)</i>
        </span>
    }

    return (
        <TableRow className="border-none hover:bg-transparent">
            <TableCell 
                colSpan={4} 
                className='px-4 py-6 text-center bg-[#0c0e16]/30 border border-white/[0.04] rounded-xl text-sm font-semibold text-slate-400'
            >
                {content}
            </TableCell>
        </TableRow>
    )
}

/**
 * Sortable table header
 */
type SortableTableHeaderProps = {
    label: string;
    sortKey: 'playTime' | 'tsJoined' | 'tsLastConnection';
    sortingState: PlayersTableSortingType;
    setSorting: (newState: PlayersTableSortingType) => void;
    className?: string;
}

function SortableTableHeader({ label, sortKey, sortingState, setSorting, className }: SortableTableHeaderProps) {
    const isSorted = sortingState.key === sortKey;
    const isDesc = sortingState.desc;
    const sortIcon = isSorted ? (isDesc ? ' ▼' : ' ▲') : '';
    const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        setSorting({
            key: sortKey,
            desc: isSorted ? (!isDesc) : true
        });
    }
    return (
        <th
            onClick={onClick}
            className={cn(
                'py-3 px-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-white transition-colors duration-150',
                isSorted && 'text-[#00ffd1]',
                className,
            )}
        >
            <span className="flex items-center gap-1">
                {label}
                <span className="text-[10px] text-muted-foreground font-mono">{sortIcon}</span>
            </span>
        </th>
    )
}

/**
 * Players table
 */
type PlayersTableProps = {
    search: PlayersTableSearchType;
    filters: PlayersTableFiltersType;
}

export default function PlayersTable({ search, filters }: PlayersTableProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [players, setPlayers] = useState<PlayersTablePlayerType[]>([]);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [sorting, setSorting] = useState<PlayersTableSortingType>({ key: 'tsJoined', desc: true });
    const [isResetting, setIsResetting] = useState(false);
    const openPlayerModal = useOpenPlayerModal();

    const playerListingApi = useBackendApi<PlayersTableSearchResp>({
        method: 'GET',
        path: '/player/search',
        abortOnUnmount: true,
    });

    const fetchNextPage = async (resetOffset?: boolean) => {
        setIsFetching(true);
        setLoadError(null);
        if (resetOffset) {
            setIsResetting(true);
        }
        const handleError = (error: string) => {
            setLoadError(error);
            if (resetOffset) {
                setPlayers([]);
            }
        }
        try {
            const queryParams: { [key: string]: string | number | boolean } = {
                sortingKey: sorting.key,
                sortingDesc: sorting.desc,
            };
            if (search.value) {
                queryParams.searchValue = search.value;
                queryParams.searchType = search.type;
            }
            if (filters.length) {
                queryParams.filters = filters.join(',');
            }
            if (!resetOffset && players.length) {
                queryParams.offsetParam = players[players.length - 1][sorting.key];
                queryParams.offsetLicense = players[players.length - 1].license;
            }
            const resp = await playerListingApi({ queryParams });

            //Dealing with errors
            if (resp === undefined) {
                return handleError(`Request failed.`);
            } else if ('error' in resp) {
                return handleError(`Request failed: ${resp.error}`);
            }

            //Setting the states
            setLoadError(null);
            setHasReachedEnd(resp.hasReachedEnd);
            setIsResetting(false);
            if (resp.players.length) {
                setPlayers((prev) => resetOffset ? resp.players : [...prev, ...resp.players]);
            } else {
                setPlayers([]);
            }
        } catch (error) {
            handleError(`Failed to fetch more data: ${(error as Error).message}`);
        } finally {
            setIsFetching(false);
            setIsResetting(false);
        }
    };

    // The virtualizer
    const rowVirtualizer = useVirtualizer({
        isScrollingResetDelay: 0,
        count: players.length + 1,
        getScrollElement: () => (scrollRef.current as HTMLDivElement)?.getElementsByTagName('div')[0],
        estimateSize: () => 70, // spaced rows
        overscan: 25,
    });
    const virtualItems = rowVirtualizer.getVirtualItems();
    const virtualizerTotalSize = rowVirtualizer.getTotalSize();

    //NOTE: This is required due to how css works on tables
    //ref: https://github.com/TanStack/virtual/issues/585
    let TopRowPad: React.ReactNode = null;
    let BottomRowPad: React.ReactNode = null;
    if (virtualItems.length > 0) {
        const padStart = virtualItems[0].start - rowVirtualizer.options.scrollMargin;
        if (padStart > 0) {
            TopRowPad = <tr><td colSpan={4} style={{ height: padStart }} /></tr>;
        }
        const padEnd = virtualizerTotalSize - virtualItems[virtualItems.length - 1].end;
        if (padEnd > 0) {
            BottomRowPad = <tr><td colSpan={4} style={{ height: padEnd }} /></tr>;
        }
    }

    // Automagically fetch next page when reaching the end
    useEffect(() => {
        if (!players.length || !virtualItems.length) return;
        const lastVirtualItemIndex = virtualItems[virtualItems.length - 1].index;
        if (players.length <= lastVirtualItemIndex && !hasReachedEnd && !isFetching) {
            fetchNextPage()
        }
    }, [players, virtualItems, hasReachedEnd, isFetching]);

    //on state change, reset the list
    useEffect(() => {
        rowVirtualizer.scrollToIndex(0);
        fetchNextPage(true);
    }, [search, filters, sorting]);

    return (
        <div
            className="w-full max-h-full min-h-96 overflow-auto border border-white/[0.05] bg-[#070913]/30 md:rounded-xl shadow-inner"
            style={{ overflowAnchor: "none" }}
        >
            <ScrollArea className="h-full" ref={scrollRef}>
                <table className='w-full caption-bottom text-sm select-none border-separate border-spacing-y-1.5 px-4 pb-4'>
                    <TableHeader>
                        <tr className='sticky top-0 z-10 bg-slate-950/90 text-secondary-foreground text-base shadow-sm transition-colors backdrop-blur-md'>
                            <th className='py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-left select-none'>
                                Player Details
                            </th>
                            <SortableTableHeader
                                label='Play Time'
                                sortKey='playTime'
                                sortingState={sorting}
                                setSorting={setSorting}
                            />
                            <SortableTableHeader
                                label='First Joined'
                                sortKey='tsJoined'
                                sortingState={sorting}
                                setSorting={setSorting}
                            />
                            <SortableTableHeader
                                label='Last Connection'
                                sortKey='tsLastConnection'
                                sortingState={sorting}
                                setSorting={setSorting}
                            />
                        </tr>
                    </TableHeader>
                    <TableBody className={cn(isResetting && 'opacity-25')}>
                        {TopRowPad}
                        {virtualItems.map((virtualItem) => {
                            const isLastRow = virtualItem.index > players.length - 1;
                            return isLastRow ? (
                                <LastRow
                                    key={virtualItem.key}
                                    playersCount={players.length}
                                    hasReachedEnd={hasReachedEnd}
                                    loadError={loadError}
                                    isFetching={isFetching}
                                    retryFetch={fetchNextPage}
                                />
                            ) : (
                                <PlayerRow
                                    key={virtualItem.key}
                                    rowData={players[virtualItem.index]}
                                    modalOpener={openPlayerModal}
                                />
                            )
                        })}
                        {BottomRowPad}
                    </TableBody>
                </table>
            </ScrollArea>
        </div>
    );
}
